import SmallEmpty from '@/components/ux/SmallEmpty';
import SortIcon, { getNextSortOrder } from '@/components/ux/SortIcon';
import EmptyStateIcon from '@/pages/signup-forms/components/EmptyStateIcon';
import type { CampaignsType, CampaignSortFields, ContactSortField } from '@/types';
import { useCampaignsStore, useCampaignsTableStore, useSearchStore } from '@/stores';
import { Flex, Table, TablePagination, TBody, Th, THead, Tooltip, TooltipContent, TooltipTrigger, Tr, Typography } from '@alpine-iq/ui-library';
import type { CampaignColumnConfig } from './CampaignsTableHeaders';
import React from 'react';
import type { FiltersData } from '../filters';
import { CampaignsTableHeaders } from './CampaignsTableHeaders';
import { onSelectCampaign } from '../CampaignsPage';
import CampaignMoreButton from './CampaignMoreButton';

type CampaignsTableProps = {
	filters: Partial<FiltersData>;
};

export default function CampaignsTable({ filters }: CampaignsTableProps) {
	const search = useSearchStore((state) => state.search);
	const { loading, campaignsMap, pagination, setPage, loaded } = useCampaignsStore();

	const { refreshTable } = useCampaignsTableStore();
	const [sortMap, _setSortMap] = React.useState<Partial<Record<keyof CampaignSortFields, ContactSortField>>>({});

	const { page, limit } = pagination ?? {
		page: 1,
		limit: 10,
	};

	// If page is > maxPage then we go to 1
	React.useEffect(() => {
		const maxPage = Math.ceil((pagination?.totalResult ?? 0) / limit);
		if (page > maxPage) {
			setPage(1);
		}
	}, [page, pagination?.totalResult, limit, setPage]);

	const tableData = React.useMemo(() => {
		const campaigns: CampaignsType[] = Object.values(campaignsMap ?? {});
		const templateData: CampaignsType[] = campaigns.sort((a, b) => (b.created ?? 0) - (a.created ?? 0));
		return templateData;
	}, [campaignsMap, page, limit]);

	const start = (page - 1) * limit;

	const { ActiveHeaders, TableHeaders } = React.useMemo(() => {
		const multiSorting = Object.values(sortMap).filter((sort) => sort.direction).length > 1;
		const ActiveHeaders = (Object.entries(CampaignsTableHeaders)
			.sort(([, a], [, b]) => (a.settings().columnOrder ?? 0) - (b.settings().columnOrder ?? 0))
			.filter(([, { enabled }]) => enabled()) ?? []) as Array<[keyof CampaignSortFields, CampaignColumnConfig]>;
		return {
			ActiveHeaders,
			TableHeaders: ActiveHeaders.map(([sortKey, { label }]) => {
				const canSort = sortKey !== undefined;
				const sorter = sortMap[sortKey ?? ('' as keyof CampaignSortFields)];
				const { priority, direction } = sorter ?? {};

				const sortingMoreThanOne = multiSorting && priority !== undefined;
				const nextSortOrder = getNextSortOrder(direction);

				return (
					<Th
						key={label}
						css={{ cursor: canSort ? 'pointer' : 'default', userSelect: 'none' }}
					>
						<Tooltip open={false}>
							<TooltipTrigger asChild>
								<Flex
									align="center"
									style={{ maxWidth: 'max-content' }}
								>
									{label}
									{canSort && (
										<SortIcon
											sortOrder={direction}
											prority={sortingMoreThanOne ? priority || 0 : undefined}
										/>
									)}
								</Flex>
							</TooltipTrigger>
							{canSort && (
								<>
									<TooltipContent style={{ zIndex: 1000 }}>
										{nextSortOrder === undefined && `Click to cancel sorting by ${label}`}
										{nextSortOrder === 'asc' && `Click to sort ${label} from least to greatest`}
										{nextSortOrder === 'desc' && `Click to sort ${label} from greatest to least`}
									</TooltipContent>
								</>
							)}
						</Tooltip>
					</Th>
				);
			}),
		};
	}, [sortMap, refreshTable]);

	const isSearching = search.length > 0;
	const hasCampaigns = !!Object.keys(campaignsMap).length;

	if (!hasCampaigns && loaded && !loading && !isSearching) {
		return null;
	}

	return (
		<>
			<div
				css={`
					// Target the first div child of this component
					> div:first-child {
						overflow-y: hidden !important;
						overflow-x: auto !important;
					}
				`}
			>
				<Table
					scrollable
					loadingColumns={5}
					loading={!loaded || loading}
					pageSize={!loaded || loading ? 2 : 1}
				>
					<THead>
						<Tr>{TableHeaders}</Tr>
					</THead>
					<TBody>
						{(tableData ?? []).map((campaign) => {
							return (
								<Tr
									key={campaign.id}
									onClick={(e) => {
										// If they clicked the dropdown ignore the click
										if ((e.target as HTMLElement).closest('.dispense-dropdown-menu')) return;
										if ((e.target as HTMLElement).closest('.campaign-tag')) return;
										if ((e.target as HTMLElement).closest('.campaign-tag-input-container')) return;

										onSelectCampaign(campaign.id ?? '');
									}}
									showMenu
									menuContent={(close) => (
										<>
											<CampaignMoreButton
												id={campaign.id}
												close={close}
												campaign={campaign}
											/>
										</>
									)}
									css={`
										.campaign-tag-input-container {
											opacity: 0;
											display: flex;
										}

										&:hover {
											.campaign-tag-input-container {
												opacity: 1;
											}
										}
									`}
								>
									{ActiveHeaders.map(([sortKey, { render }], index) => {
										return <React.Fragment key={`${campaign.id}-${index}`}>{render(campaign, { search })}</React.Fragment>;
									})}
								</Tr>
							);
						})}
						{!tableData.length && (
							<SmallEmpty
								style={{ height: 350 }}
								content={
									<Flex
										center
										column
										gap="24"
									>
										<EmptyStateIcon />
										<Typography variant="h2">{search ? 'No results found' : 'No campaign forms'}</Typography>
									</Flex>
								}
							/>
						)}
					</TBody>
				</Table>
			</div>

			<TablePagination
				fromCount={(start ?? 0) + 1}
				toCount={(start ?? 0) + (limit ?? 0)}
				totalCount={pagination?.totalResult ?? 0}
				loading={loading}
				nextDisabled={loading || (pagination?.totalResult ?? 0) <= (start ?? 0) + (limit ?? 0)}
				prevDisabled={loading || (start ?? 0) === 0}
				onNextClick={() => setPage(Math.min(Number(page) + 1, Math.ceil((pagination?.totalResult ?? 0) / Number(limit))))}
				onPrevClick={() => setPage(Math.max(Number(page) - 1, 1))}
				style={{ paddingBlock: 0, paddingBottom: 80 }}
			/>
		</>
	);
}
